<template>
  <div class="h-full overflow-y-auto">
    <!-- 表格头部 -->
    <div class="sticky top-0 bg-white dark:bg-gray-800 z-10 border-b border-gray-200 dark:border-gray-700">
      <div class="flex min-w-full">
        <div class="w-[5%] py-3.5 px-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">ID</div>
        <div class="w-[9%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">周期类型</div>
        <div class="w-[7%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">天数</div>
        <div class="w-[9%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">价格</div>
        <div class="w-[9%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">原价</div>
        <div class="w-[7%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">折扣</div>
        <div class="w-[6%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">排序</div>
        <div class="w-[9%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">类型</div>
        <div class="w-[9%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">购买限制</div>
        <div class="w-[12%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">状态</div>
        <div class="w-[18%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">操作</div>
      </div>
    </div>

    <!-- 表格内容 -->
    <div class="divide-y divide-gray-200 dark:divide-gray-700">
      <div v-for="pricing in pricings" :key="pricing.id"
        class="flex items-center min-w-full hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors duration-150"
        :class="{ 'bg-gray-100 dark:bg-gray-700 opacity-60': pricing.isHidden }">
        <div class="w-[5%] py-4 px-3 text-sm text-gray-900 dark:text-gray-100">{{ pricing.id }}</div>
        <div class="w-[9%] px-3 py-4 text-sm">
          <span :class="{
            'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400': pricing.durationType === 'MONTHLY',
            'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400': pricing.durationType === 'QUARTERLY',
            'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400': pricing.durationType === 'YEARLY',
            'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400': pricing.durationType === 'CUSTOM',
          }" class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium">
            {{ durationTypeLabels[pricing.durationType] }}
          </span>
        </div>
        <div class="w-[7%] px-3 py-4 text-sm text-gray-600 dark:text-gray-400">{{ pricing.durationDays }}</div>
        <div class="w-[9%] px-3 py-4 text-sm text-gray-900 dark:text-gray-100 font-medium">
          ¥{{ (pricing.price / 100).toFixed(2) }}
        </div>
        <div class="w-[9%] px-3 py-4 text-sm text-gray-400">
          {{ pricing.originalPrice ? `¥${(pricing.originalPrice / 100).toFixed(2)}` : '-' }}
        </div>
        <div class="w-[7%] px-3 py-4 text-sm text-orange-600 dark:text-orange-400 font-medium">
          {{ pricing.discountPercentage ? `${(pricing.discountPercentage * 100).toFixed(0)}折` : '-' }}
        </div>
        <div class="w-[6%] px-3 py-4 text-sm text-gray-600 dark:text-gray-400 font-medium">
          {{ pricing.displayOrder }}
        </div>
        <div class="w-[9%] px-3 py-4 text-sm">
          <span v-if="pricing.isOneTime"
            class="inline-flex items-center rounded-full bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 text-xs font-medium text-yellow-800 dark:text-yellow-400">
            一次性
          </span>
          <span v-else class="text-gray-500 dark:text-gray-400">常规</span>
        </div>
        <div class="w-[9%] px-3 py-4 text-sm text-gray-600 dark:text-gray-400">
          {{ pricing.isOneTime && pricing.maxPurchaseCount ? `最多 ${pricing.maxPurchaseCount} 次` : '-' }}
        </div>
        <div class="w-[12%] px-3 py-4 text-sm">
          <div class="flex flex-col gap-1">
            <span v-if="pricing.isHot"
              class="inline-flex items-center rounded-full bg-red-50 dark:bg-red-900/30 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-400">
              🔥 热门
            </span>
            <span v-if="pricing.isHidden" @click.stop="handleToggleHidden(pricing)"
              class="inline-flex items-center rounded-full bg-gray-200 dark:bg-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors">
              👁️ 已隐藏
            </span>
            <span v-else @click.stop="handleToggleHidden(pricing)"
              class="inline-flex items-center rounded-full bg-green-50 dark:bg-green-900/30 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors">
              ✓ 显示中
            </span>
          </div>
        </div>
        <div class="w-[18%] px-3 py-4 text-sm">
          <div class="flex gap-2">
            <button @click.stop="handleEdit(pricing)" class="link-btn-edit font-medium">
              编辑
            </button>
            <button @click.stop="handleDelete(pricing)" class="link-btn-delete font-medium">
              删除
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="pricings.length === 0" class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">暂无价格方案</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">为该套餐创建第一个价格方案吧</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PlanPricing } from '@/types/membership/pricing'
import { DurationTypeLabels } from '@/types/membership/enums'

/**
 * Props
 */
interface Props {
  /** 价格方案列表 */
  pricings: PlanPricing[]
}

defineProps<Props>()

/**
 * Emits
 */
const emit = defineEmits<{
  /** 编辑价格方案 */
  edit: [pricing: PlanPricing]
  /** 删除价格方案 */
  delete: [pricing: PlanPricing]
  /** 切换隐藏状态 */
  toggleHidden: [pricing: PlanPricing]
}>()

/**
 * 周期类型标签映射
 */
const durationTypeLabels = DurationTypeLabels

/**
 * 处理编辑
 */
const handleEdit = (pricing: PlanPricing) => {
  emit('edit', pricing)
}

/**
 * 处理删除
 */
const handleDelete = (pricing: PlanPricing) => {
  emit('delete', pricing)
}

/**
 * 处理切换隐藏状态
 */
const handleToggleHidden = (pricing: PlanPricing) => {
  emit('toggleHidden', pricing)
}
</script>

<style scoped>
/* 可选：添加自定义样式 */
</style>
